import * as React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { registerServiceWorker } from './serviceWorkerRegistration';
import { errorCaptureService } from './services/errorCaptureService';

// ─── Reload-loop detection ──────────────────────────────────────────────────
// Tracks page loads in sessionStorage. If the page reloads too many times
// within a short window it's almost certainly a SW or code bug causing a loop.
const RELOAD_LOOP_KEY = '__reload_loop';
const RELOAD_LOOP_WINDOW_MS = 15_000; // 15-second window
const RELOAD_LOOP_THRESHOLD = 3;      // 3 reloads in that window = loop

(() => {
  const now = Date.now();
  let history: number[] = [];
  try {
    history = JSON.parse(sessionStorage.getItem(RELOAD_LOOP_KEY) || '[]');
  } catch { /* corrupt data, start fresh */ }

  // Keep only recent entries
  history = history.filter(t => now - t < RELOAD_LOOP_WINDOW_MS);
  history.push(now);
  sessionStorage.setItem(RELOAD_LOOP_KEY, JSON.stringify(history));

  console.log(`[ReloadLoop] Load #${history.length} in ${RELOAD_LOOP_WINDOW_MS / 1000}s window (threshold: ${RELOAD_LOOP_THRESHOLD})`);

  if (history.length >= RELOAD_LOOP_THRESHOLD) {
    console.error(
      `[ReloadLoop] Page reloaded ${history.length} times in ${RELOAD_LOOP_WINDOW_MS / 1000}s — possible reload loop detected. ` +
      `Unregistering service worker to break the cycle.`
    );
    // Break the loop: unregister the SW and clear caches so next load is clean
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        console.log(`[ReloadLoop] Unregistering ${regs.length} service worker(s)`);
        regs.forEach(r => r.unregister());
      });
      caches.keys().then(names => {
        console.log(`[ReloadLoop] Clearing ${names.length} cache(s):`, names);
        names.forEach(n => caches.delete(n));
      });
    }
    // Clear the counter so after this "recovery" load we start fresh
    sessionStorage.removeItem(RELOAD_LOOP_KEY);
  }
})();

// Initialize error capture before render so errors during initial render are captured
errorCaptureService.initialize();

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Register service worker for offline functionality in all environments.
registerServiceWorker();

